import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface AlbumCache {
  _id: string;
  title: string;
  artist: string;
  tracklist: any[];
  printUnits: any;
  createdAt: string;
  updatedAt: string;
  cachedAt: number;
}

interface PendingChange {
  id: string;
  type: 'create' | 'update' | 'delete';
  data: any;
  timestamp: number;
}

interface MDLabelDB extends DBSchema {
  albums: {
    key: string;
    value: AlbumCache;
    indexes: { 'by-updated': string };
  };
  pendingChanges: {
    key: string;
    value: PendingChange;
    indexes: { 'by-timestamp': number };
  };
}

const DB_NAME = 'md-label-maker-cache';
const DB_VERSION = 1;
const MAX_CACHE_SIZE = 500; // Max cached albums
const CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days

let dbPromise: Promise<IDBPDatabase<MDLabelDB>> | null = null;

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB<MDLabelDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Albums store
        const albumStore = db.createObjectStore('albums', { keyPath: '_id' });
        albumStore.createIndex('by-updated', 'updatedAt');

        // Pending changes store
        const pendingStore = db.createObjectStore('pendingChanges', { keyPath: 'id' });
        pendingStore.createIndex('by-timestamp', 'timestamp');
      },
    });
  }
  return dbPromise;
}

// Cache albums
export async function cacheAlbums(albums: any[]): Promise<void> {
  const db = await getDB();
  const tx = db.transaction('albums', 'readwrite');
  const now = Date.now();

  for (const album of albums) {
    await tx.store.put({
      ...album,
      cachedAt: now,
    });
  }
  await tx.done;

  // Cleanup old entries
  await cleanupOldCache();
}

// Get cached albums
export async function getCachedAlbums(): Promise<AlbumCache[]> {
  const db = await getDB();
  const albums = await db.getAll('albums');
  // Filter expired
  const now = Date.now();
  return albums.filter((a) => now - a.cachedAt < CACHE_TTL);
}

// Cache single album
export async function cacheAlbum(album: any): Promise<void> {
  const db = await getDB();
  await db.put('albums', { ...album, cachedAt: Date.now() });
}

// Get cached album by ID
export async function getCachedAlbum(id: string): Promise<AlbumCache | undefined> {
  const db = await getDB();
  const album = await db.get('albums', id);
  if (album && Date.now() - album.cachedAt < CACHE_TTL) {
    return album;
  }
  return undefined;
}

// Add pending change (for offline edits)
export async function addPendingChange(change: Omit<PendingChange, 'timestamp'>): Promise<void> {
  const db = await getDB();
  await db.put('pendingChanges', { ...change, timestamp: Date.now() });
}

// Get all pending changes
export async function getPendingChanges(): Promise<PendingChange[]> {
  const db = await getDB();
  return db.getAllFromIndex('pendingChanges', 'by-timestamp');
}

// Clear a pending change after sync
export async function clearPendingChange(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('pendingChanges', id);
}

// Clear all pending changes
export async function clearAllPendingChanges(): Promise<void> {
  const db = await getDB();
  await db.clear('pendingChanges');
}

// Cleanup old cache entries
async function cleanupOldCache(): Promise<void> {
  const db = await getDB();
  const allAlbums = await db.getAll('albums');
  const now = Date.now();

  // Remove expired
  const tx = db.transaction('albums', 'readwrite');
  for (const album of allAlbums) {
    if (now - album.cachedAt > CACHE_TTL) {
      await tx.store.delete(album._id);
    }
  }
  await tx.done;

  // If still too many, remove oldest
  const remaining = await db.getAll('albums');
  if (remaining.length > MAX_CACHE_SIZE) {
    remaining.sort((a, b) => a.cachedAt - b.cachedAt);
    const toRemove = remaining.slice(0, remaining.length - MAX_CACHE_SIZE);
    const tx2 = db.transaction('albums', 'readwrite');
    for (const album of toRemove) {
      await tx2.store.delete(album._id);
    }
    await tx2.done;
  }
}

// Check if online
export function isOnline(): boolean {
  return navigator.onLine;
}

// Sync pending changes when back online
export async function syncPendingChanges(
  syncHandler: (change: PendingChange) => Promise<boolean>
): Promise<{ synced: number; failed: number }> {
  const changes = await getPendingChanges();
  let synced = 0;
  let failed = 0;

  for (const change of changes) {
    try {
      const success = await syncHandler(change);
      if (success) {
        await clearPendingChange(change.id);
        synced++;
      } else {
        failed++;
      }
    } catch {
      failed++;
    }
  }

  return { synced, failed };
}
